import {
    type FC
} from "react";
import {
    Pressable,
    View
} from "react-native";
import type IFileUploadAvatarUploadProps from "./type";
import stylesheet, {
    getAvatarUploadBadge,
    AVATAR_UPLOAD_SIZES,
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../../../core/hooks";
import {
    useInteractionState
} from "../../../../helpers/interaction";
import {
    Pencil as PencilIcon,
    Upload as UploadIcon
} from "lucide-react-native";
import {
    getFocusRingStyle
} from "../../../../helpers/theme/state";
import {
    formatPercent
} from "../../../../helpers/locale";
import {
    formatFileProgress
} from "../../util";
import StateLayer from "../../../stateLayer";
import Progress from "../../../progress";
import Avatar from "../../../avatar";
import Button from "../../../button";
import Text from "../../../text";

const FileUploadAvatarUpload: FC<IFileUploadAvatarUploadProps> = ({
    isDisabled = false,
    customLocalize,
    descriptionId,
    customTheme,
    description,
    onRemove,
    isTouch,
    onPress,
    style,
    title,
    file,
    size
}) => {
    const {
        controlHeights,
        touchTargets,
        avatarSizes,
        shadowAlpha,
        elevations,
        iconSizes,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        activeLocale,
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const {
        interactionHandlers,
        isFocusVisible,
        isHovered,
        isPressed
    } = useInteractionState({
        isDisabled
    });

    const {
        container: containerDynamicStyle,
        actions: actionsDynamicStyle,
        badge: badgeDynamicStyle,
        media: mediaDynamicStyle,
        body: bodyDynamicStyle,
        disc: discDynamicStyle,
        text: textDynamicStyle
    } = useStyles({
        controlHeights,
        touchTargets,
        avatarSizes,
        shadowAlpha,
        elevations,
        isDisabled,
        iconSizes,
        borders,
        colors,
        spaces,
        size
    });

    const badge = getAvatarUploadBadge({
        controlHeights,
        touchTargets,
        iconSizes,
        borders,
        size
    });

    const status = file?.status;

    const isUploading = status === "uploading" || status === "processing";

    const getRuleText = () => {
        if(!isUploading || !file) {
            return description;
        }

        const parts = [
            formatPercent({
                value: (file.progress ?? 0) / 100,
                locale: activeLocale
            })
        ];

        if(file.size !== undefined) {
            parts.push(formatFileProgress({
                loaded: file.size * (file.progress ?? 0) / 100,
                locale: activeLocale,
                total: file.size
            }));
        }

        return localize("file-upload-uploading", [
            parts.join(" · ")
        ]);
    };

    const ruleText = getRuleText();

    const renderProgress = () => {
        if(!isUploading) {
            return null;
        }

        return <View
            style={stylesheet.progress}
        >
            <View
                style={[
                    stylesheet.disc,
                    discDynamicStyle
                ]}
            >
                <Progress
                    title={localize(status === "uploading" ? "attachment-uploading" : "attachment-processing", [
                        file?.name ?? ""
                    ])}
                    isIndeterminate={status === "processing" || file?.progress === undefined}
                    customLocalize={customLocalize}
                    value={file?.progress ?? 0}
                    customTheme={customTheme}
                    variant="circular"
                    size={size}
                >
                    <Progress.Track>
                        <Progress.Indicator />
                    </Progress.Track>
                </Progress>
            </View>
        </View>;
    };

    const renderBadge = () => {
        if(isUploading) {
            return null;
        }

        return <View
            style={[
                stylesheet.badge,
                badgeDynamicStyle
            ]}
            aria-hidden={true}
        >
            <PencilIcon
                color={isDisabled ? colors.system.state.content.disabled.neutral : colors.content.icon.mid}
                size={badge.iconSize}
            />
        </View>;
    };

    const renderActions = () => {
        if(size !== "large" || isTouch) {
            return null;
        }

        return <View
            style={[
                stylesheet.actions,
                actionsDynamicStyle
            ]}
        >
            <Button
                icon={({
                    size: iconSize,
                    customColor,
                    color
                }) => <UploadIcon
                    color={customColor ?? colors.content.icon[color]}
                    size={iconSize}
                />}
                title={localize("file-upload-upload-photo")}
                customTheme={customTheme}
                isDisabled={isDisabled}
                spreadBehaviour="free"
                variant="outline"
                onPress={onPress}
                type="neutral"
                size="small"
            />
            {
                file && onRemove ?
                    <Button
                        accessibilityLabel={localize("remove-attachment", [
                            file.name
                        ])}
                        title={localize("remove")}
                        customTheme={customTheme}
                        isDisabled={isDisabled}
                        spreadBehaviour="free"
                        onPress={onRemove}
                        variant="ghost"
                        type="neutral"
                        size="small"
                    />
                :
                    null
            }
        </View>;
    };

    return <View
        style={[
            style,
            stylesheet.container,
            containerDynamicStyle
        ]}
    >
        <Pressable
            {...interactionHandlers}
            accessibilityLabel={localize("file-upload-change-photo")}
            style={[
                stylesheet.media,
                isDisabled ? stylesheet.mediaDisabled : undefined
            ]}
            {...{
                "aria-describedby": descriptionId
            }}
            aria-disabled={isDisabled}
            aria-busy={isUploading}
            disabled={isDisabled}
            onPress={onPress}
            role="button"
        >
            <View
                style={[
                    mediaDynamicStyle,
                    getFocusRingStyle({
                        isFocusVisible,
                        borders,
                        colors
                    })
                ]}
            >
                <Avatar
                    size={AVATAR_UPLOAD_SIZES[size]}
                    imageUrl={file?.previewUri}
                    customTheme={customTheme}
                    isDisabled={isDisabled}
                    title={title}
                />
                <StateLayer
                    borderRadius={avatarSizes[AVATAR_UPLOAD_SIZES[size]]}
                    isHovered={!isDisabled && isHovered}
                    isPressed={!isDisabled && isPressed}
                    borderWidth={borders.line}
                    customTheme={customTheme}
                    overlayColor="default"
                />
                {renderProgress()}
                {renderBadge()}
            </View>
        </Pressable>
        <View
            style={[
                stylesheet.body,
                bodyDynamicStyle
            ]}
        >
            <View
                style={[
                    stylesheet.text,
                    textDynamicStyle
                ]}
            >
                {
                    title ?
                        <Text
                            customColor={isDisabled ? colors.system.state.content.disabled.neutral : undefined}
                            color={isDisabled ? undefined : "high"}
                            customTheme={customTheme}
                            variant="titleSmallSize"
                        >
                            {title}
                        </Text>
                    :
                        null
                }
                {
                    ruleText ?
                        <Text
                            customColor={isDisabled ? colors.system.state.content.disabled.neutral : undefined}
                            color={isDisabled ? undefined : "mid"}
                            customTheme={customTheme}
                            style={stylesheet.rule}
                            variant="bodySmallSize"
                            id={descriptionId}
                        >
                            {ruleText}
                        </Text>
                    :
                        null
                }
            </View>
            {renderActions()}
        </View>
    </View>;
};
export default FileUploadAvatarUpload;
